import { computed, inject } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { setError, setLoaded, setUpdating, withCallState } from "@dcc/shared/state";
import { ConsentClient, UserStore } from "@dcc/vestas-online/data-access";
import { VoConsentAgreementSlug } from "@dcc/vestas-online/domain";
import { tapResponse } from "@ngrx/operators";
import { patchState, signalStore, withComputed, withMethods, withProps } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { exhaustMap, pipe } from "rxjs";

function getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === "object" && error !== null && "message" in error) {
        const message = (error as { message?: unknown }).message;
        if (typeof message === "string") {
            return message;
        }
    }

    if (typeof error === "string") {
        return error;
    }

    return String(error);
}

export const TosStore = signalStore(
    withCallState(),
    withProps(() => {
        const consentClient = inject(ConsentClient);
        const userStore = inject(UserStore);
        const router = inject(Router);

        const termsResource = rxResource({
            params: () => userStore.language(),
            stream: ({ params: lang }) => consentClient.getAgreementBySlug(VoConsentAgreementSlug, lang)
        });

        const consentStatusResource = rxResource({
            stream: () => consentClient.getConsentStatus()
        });

        return {
            _consentClient: consentClient,
            _router: router,
            termsResource: termsResource.asReadonly(),
            consentStatusResource: consentStatusResource.asReadonly()
        };
    }),
    withComputed(({ consentStatusResource }) => ({
        shouldAcceptTerms: computed(() => {
            if (!consentStatusResource.hasValue()) return false;
            const status = consentStatusResource.value();
            const agreement = status.agreements.find((a) => a.slug === VoConsentAgreementSlug);
            if (!agreement) return true;
            return !agreement.hasConsented || agreement.requiresReconsent;
        })
    })),
    withMethods((store) => ({
        acceptTerms: rxMethod<string>(
            pipe(
                exhaustMap((versionId) => {
                    patchState(store, setUpdating());

                    return store._consentClient.acceptAgreement(versionId).pipe(
                        tapResponse({
                            next: () => {
                                patchState(store, setLoaded());
                                void store._router.navigate(["app", "jumpboard"]);
                            },
                            error: (err: unknown) => patchState(store, setError(getErrorMessage(err)))
                        })
                    );
                })
            )
        )
    }))
);
